import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, Search, RefreshCw, User, Mail, Phone, ArrowLeft, IndianRupee,
  TrendingUp, RotateCcw, Calendar, CreditCard, BadgeCheck, Briefcase, Building2,
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { api, formatApiErrorDetail } from '../../lib/api';
import { toast } from 'sonner';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const KpiTile = ({ label, value, accent = 'gray', testId, icon: Icon }) => (
  <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800" data-testid={testId}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
        {Icon && <Icon className={`h-4 w-4 text-${accent}-500`} />}
      </div>
      <p className={`mt-1 text-xl sm:text-2xl font-bold text-${accent}-700 dark:text-${accent}-300`}>{value}</p>
    </CardContent>
  </Card>
);

const STATUS_STYLE = {
  paid:     'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  pending:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  refunded: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
};

// ─────────────────────────────────────────────────────────────────────────────
// Customer list
// ─────────────────────────────────────────────────────────────────────────────
export const AdminCustomers = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/crm/customers', { params: q ? { q } : {} });
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="admin-customers-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Customers</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{items.length} individual customer{items.length === 1 ? '' : 's'}</p>
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh</Button>
      </div>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-3">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="h-9 pl-8 text-sm" data-testid="customer-search-input" />
            </div>
            <Button type="submit" size="sm">Search</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No customers yet. Seed dummy invoices to populate.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Customer</th>
                  <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Bookings</th>
                  <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Revenue</th>
                  <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Profit</th>
                  <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Refunds</th>
                  <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Pending</th>
                  <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Last Booking</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {items.map((c) => (
                  <tr key={c.email}
                      className="hover:bg-red-50/40 dark:hover:bg-red-900/10 cursor-pointer"
                      onClick={() => navigate(`/admin/customers/${encodeURIComponent(c.email)}`)}
                      data-testid={`customer-row-${c.email}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900 dark:text-white">{c.name}</p>
                      <p className="text-xs text-gray-500">{c.email} · {c.phone}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{c.total_bookings}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-700 dark:text-emerald-300">{fmtINR(c.revenue)}</td>
                    <td className="px-4 py-3 text-right font-bold text-blue-700 dark:text-blue-300">{fmtINR(c.profit)}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{fmtINR(c.refunds)}</td>
                    <td className="px-4 py-3 text-right text-amber-700 dark:text-amber-300">{fmtINR(c.pending_amount)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(c.last_booking_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Customer profitability profile
// ─────────────────────────────────────────────────────────────────────────────
export const AdminCustomerProfile = () => {
  const { email } = useParams();
  const decoded = decodeURIComponent(email || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/crm/customers/${encodeURIComponent(decoded)}`)
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to load customer'))
      .finally(() => setLoading(false));
  }, [decoded]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>;
  if (!data) return null;

  const { customer, metrics, ledger, monthly_trend: trend = [], by_booking_type: bt = [] } = data;
  const ledgerTotals = ledger.reduce((acc, r) => {
    acc.revenue += r.revenue || 0;
    acc.profit += r.profit || 0;
    acc.refund += r.refund || 0;
    return acc;
  }, { revenue: 0, profit: 0, refund: 0 });
  ledgerTotals.net_profit = ledgerTotals.profit - ledgerTotals.refund;

  return (
    <div className="space-y-6" data-testid="customer-profile-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/customers')}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
      </div>

      {/* Header */}
      <Card className="bg-gradient-to-br from-red-50 to-white dark:from-red-950/40 dark:to-gray-900 border-red-200 dark:border-red-900/40">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{customer.name}</h1>
                <Badge className="bg-red-600 text-white capitalize">{customer.account_type}</Badge>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 flex items-center gap-2"><Mail className="h-3.5 w-3.5" />{customer.email}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2"><Phone className="h-3.5 w-3.5" />{customer.phone}</p>
              {customer.gstin && <p className="text-xs text-gray-500 mt-1">GSTIN: {customer.gstin}</p>}
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-widest text-gray-500">Lifetime Value</p>
              <p className="text-3xl font-extrabold text-red-600">{fmtINR(metrics.ltv)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiTile testId="kpi-bookings" label="Total Bookings"    value={metrics.total_bookings} icon={Calendar} accent="gray" />
        <KpiTile testId="kpi-txn"      label="Transaction Value" value={fmtINR(metrics.total_transaction_value)} icon={IndianRupee} accent="red" />
        <KpiTile testId="kpi-revenue"  label="Revenue"           value={fmtINR(metrics.revenue)} icon={TrendingUp} accent="emerald" />
        <KpiTile testId="kpi-profit"   label="Profit"            value={fmtINR(metrics.profit)} icon={BadgeCheck} accent="blue" />
        <KpiTile testId="kpi-refunds"  label="Refunds"           value={fmtINR(metrics.refunds)} icon={RotateCcw} accent="amber" />
        <KpiTile testId="kpi-avg"      label="Avg Booking Value" value={fmtINR(metrics.avg_booking_value)} icon={CreditCard} accent="gray" />
        <KpiTile testId="kpi-pending"  label="Pending Amount"    value={fmtINR(metrics.pending_amount)} icon={RotateCcw} accent="amber" />
        <KpiTile testId="kpi-cancel"   label="Cancellations"     value={metrics.cancellations} icon={RotateCcw} accent="amber" />
        <KpiTile testId="kpi-last"     label="Last Booking"      value={fmtDate(metrics.last_booking_date)} icon={Calendar} accent="gray" />
      </div>

      {/* Monthly trend chart */}
      {trend.length > 0 && (
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">Monthly Trend (Revenue · Profit · Bookings)</p>
            <div className="flex items-end gap-3 h-32">
              {trend.map((m) => {
                const max = Math.max(...trend.map((t) => t.revenue || 0), 1);
                const h = Math.max(6, Math.round(((m.revenue || 0) / max) * 100));
                return (
                  <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                    <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300">{fmtINR(m.revenue).replace(/^₹/, '')}</span>
                    <div className="w-full bg-gradient-to-t from-red-600 to-red-400 rounded-t" style={{ height: `${h}%` }} title={`Profit ${fmtINR(m.profit)} · ${m.bookings} bookings`} />
                    <span className="text-[10px] text-gray-500">{m.month}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Booking type breakdown */}
      {bt.length > 0 && (
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">By Booking Type</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {bt.map((b) => (
                <div key={b.booking_type} className="border border-gray-200 dark:border-gray-800 rounded-lg p-2.5">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500 capitalize">{b.booking_type}</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{b.bookings}</p>
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-300">{fmtINR(b.revenue)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction Ledger */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <p className="text-sm font-bold text-gray-900 dark:text-white">Transaction Ledger</p>
          </div>
          {ledger.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No transactions</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Date</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Invoice / Booking</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Type</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Revenue</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Profit</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Refund</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase tracking-wide text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-testid="customer-ledger-body">
                {ledger.map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-300">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2 text-xs text-gray-900 dark:text-white">
                      <span className="font-semibold">{r.invoice_number}</span>
                      <span className="text-gray-500"> · {r.booking_id}</span>
                    </td>
                    <td className="px-4 py-2 text-xs capitalize">{r.booking_type}</td>
                    <td className="px-4 py-2 text-xs text-right font-medium">{fmtINR(r.revenue)}</td>
                    <td className="px-4 py-2 text-xs text-right text-emerald-700 dark:text-emerald-300">{fmtINR(r.profit)}</td>
                    <td className="px-4 py-2 text-xs text-right text-amber-700 dark:text-amber-300">{r.refund > 0 ? fmtINR(r.refund) : '—'}</td>
                    <td className="px-4 py-2"><Badge className={`uppercase text-[10px] ${STATUS_STYLE[r.status] || ''}`}>{r.status}</Badge></td>
                  </tr>
                ))}
                <tr className="bg-gray-50 dark:bg-gray-900 font-bold border-t-2 border-gray-300 dark:border-gray-700">
                  <td className="px-4 py-3 text-xs uppercase tracking-wide text-gray-700 dark:text-gray-200" colSpan={3}>Totals</td>
                  <td className="px-4 py-3 text-xs text-right">{fmtINR(ledgerTotals.revenue)}</td>
                  <td className="px-4 py-3 text-xs text-right text-emerald-700 dark:text-emerald-300">{fmtINR(ledgerTotals.profit)}</td>
                  <td className="px-4 py-3 text-xs text-right text-amber-700 dark:text-amber-300">{fmtINR(ledgerTotals.refund)}</td>
                  <td className="px-4 py-3 text-xs text-right text-blue-700 dark:text-blue-300" title="Net = Profit − Refund">Net {fmtINR(ledgerTotals.net_profit)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// Corporate list + profile
// ─────────────────────────────────────────────────────────────────────────────
export const AdminCorporates = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/crm/corporates', { params: q ? { q } : {} });
      setItems(data.items || []);
    } catch (err) {
      toast.error('Failed to load corporates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-6" data-testid="admin-corporates-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Corporate Accounts</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{items.length} compan{items.length === 1 ? 'y' : 'ies'} on record</p>
      </div>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-3">
          <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by company name" className="h-9 pl-8 text-sm" data-testid="corporate-search-input" />
            </div>
            <Button type="submit" size="sm">Search</Button>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>
            : items.length === 0 ? <div className="text-center py-12 text-gray-500">No corporate accounts yet</div>
            : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Company</th>
                    <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Employees</th>
                    <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Bookings</th>
                    <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Transaction Value</th>
                    <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Profit</th>
                    <th className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-gray-500">Outstanding</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {items.map((c) => (
                    <tr key={c.company_name} className="hover:bg-red-50/40 dark:hover:bg-red-900/10 cursor-pointer"
                        onClick={() => navigate(`/admin/corporates/${encodeURIComponent(c.company_name)}`)}
                        data-testid={`corporate-row-${c.company_name}`}>
                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gray-400" />{c.company_name}</p>
                        {c.gstin && <p className="text-xs text-gray-500">GSTIN: {c.gstin}</p>}
                      </td>
                      <td className="px-4 py-3 text-right font-medium">{c.total_employees_travelling}</td>
                      <td className="px-4 py-3 text-right font-medium">{c.total_bookings}</td>
                      <td className="px-4 py-3 text-right font-bold">{fmtINR(c.transaction_value)}</td>
                      <td className="px-4 py-3 text-right text-blue-700 dark:text-blue-300 font-bold">{fmtINR(c.profit)}</td>
                      <td className="px-4 py-3 text-right text-amber-700 dark:text-amber-300">{fmtINR(c.outstanding_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
        </CardContent>
      </Card>
    </div>
  );
};

export const AdminCorporateProfile = () => {
  const { company } = useParams();
  const decoded = decodeURIComponent(company || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/crm/corporates/${encodeURIComponent(decoded)}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load corporate'))
      .finally(() => setLoading(false));
  }, [decoded]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-red-600" /></div>;
  if (!data) return null;

  const { company: co, metrics, top_travellers: tt = [], top_destinations: td = [], monthly_trend: trend = [], outstanding_invoices: outs = [], ledger = [] } = data;
  const max = Math.max(...trend.map((t) => t.spend || 0), 1);

  return (
    <div className="space-y-6" data-testid="corporate-profile-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin/corporates')}><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>

      <Card className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-950/40 dark:to-gray-900 border-blue-200 dark:border-blue-900/40">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2"><Building2 className="h-6 w-6 text-blue-600" />{co.company_name}</h1>
              {co.gstin && <p className="text-xs text-gray-500 mt-1">GSTIN: {co.gstin} · Credit Limit: {fmtINR(co.credit_limit)}</p>}
            </div>
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-widest text-gray-500">Total Profit (Net)</p>
              <p className="text-3xl font-extrabold text-blue-600">{fmtINR(metrics.profit)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiTile label="Employees Travelling" value={tt.length} accent="blue" />
        <KpiTile label="Total Bookings"       value={metrics.total_bookings} accent="gray" />
        <KpiTile label="Transaction Value"    value={fmtINR(metrics.total_transaction_value)} accent="red" />
        <KpiTile label="Revenue (Paid)"       value={fmtINR(metrics.revenue)} accent="emerald" />
        <KpiTile label="Outstanding"          value={fmtINR(metrics.pending_amount)} accent="amber" />
      </div>

      {/* Monthly spend */}
      {trend.length > 0 && (
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-gray-900 dark:text-white mb-3">Monthly Spend Trend</p>
            <div className="flex items-end gap-3 h-32">
              {trend.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                  <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300">{fmtINR(m.spend).replace(/^₹/, '')}</span>
                  <div className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t" style={{ height: `${Math.max(6, Math.round(((m.spend || 0) / max) * 100))}%` }} />
                  <span className="text-[10px] text-gray-500">{m.month}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top travellers + Top destinations side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Top Travellers</p>
            {tt.length === 0 ? <p className="text-xs text-gray-500">No data</p> : (
              <ul className="space-y-1.5">
                {tt.map((t) => (
                  <li key={t.email} className="flex items-center justify-between text-sm border-b border-gray-100 dark:border-gray-800 pb-1">
                    <span><span className="font-semibold">{t.name}</span><span className="text-xs text-gray-500 ml-2">{t.email}</span></span>
                    <span className="font-bold text-blue-600 dark:text-blue-300">{fmtINR(t.spend)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">Top Destinations</p>
            {td.length === 0 ? <p className="text-xs text-gray-500">No data</p> : (
              <ul className="space-y-1.5">
                {td.map((d) => (
                  <li key={d.destination} className="flex items-center justify-between text-sm border-b border-gray-100 dark:border-gray-800 pb-1">
                    <span className="font-medium">{d.destination}</span>
                    <span className="text-xs text-gray-500">{d.bookings} booking{d.bookings === 1 ? '' : 's'}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Outstanding invoices */}
      {outs.length > 0 && (
        <Card className="bg-white dark:bg-gray-900 border-amber-200 dark:border-amber-900/40">
          <CardContent className="p-0 overflow-x-auto">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
              <p className="text-sm font-bold text-amber-700 dark:text-amber-300">⚠ Outstanding Invoices · {outs.length}</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Invoice</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Date</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Type</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Traveller</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase text-gray-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {outs.map((o) => (
                  <tr key={o.invoice_number}>
                    <td className="px-4 py-2 text-xs font-semibold">{o.invoice_number}</td>
                    <td className="px-4 py-2 text-xs">{fmtDate(o.date)}</td>
                    <td className="px-4 py-2 text-xs capitalize">{o.booking_type}</td>
                    <td className="px-4 py-2 text-xs">{o.traveller}</td>
                    <td className="px-4 py-2 text-xs text-right font-bold text-amber-700 dark:text-amber-300">{fmtINR(o.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Ledger */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800">
        <CardContent className="p-0 overflow-x-auto">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <p className="text-sm font-bold text-gray-900 dark:text-white">Transaction Ledger</p>
          </div>
          {ledger.length === 0 ? <div className="text-center py-8 text-gray-500">No transactions</div> : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Date</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Invoice</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Type</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase text-gray-500">Revenue</th>
                  <th className="text-right px-4 py-2 text-[11px] uppercase text-gray-500">Profit</th>
                  <th className="text-left px-4 py-2 text-[11px] uppercase text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {ledger.map((r, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-xs">{fmtDate(r.date)}</td>
                    <td className="px-4 py-2 text-xs font-semibold">{r.invoice_number}</td>
                    <td className="px-4 py-2 text-xs capitalize">{r.booking_type}</td>
                    <td className="px-4 py-2 text-xs text-right">{fmtINR(r.revenue)}</td>
                    <td className="px-4 py-2 text-xs text-right text-emerald-700 dark:text-emerald-300">{fmtINR(r.profit)}</td>
                    <td className="px-4 py-2"><Badge className={`uppercase text-[10px] ${STATUS_STYLE[r.status] || ''}`}>{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
